import type { RefObject } from 'react';
import { useEffect, useState } from 'react';
import type { ILocation } from '@/Interfaces/Data';
import type { PossibleLocal } from '@/features/fare/types';

interface GoogleMapsApiArgs {
  defaultLocation?: ILocation;
  autocompleteRef?: RefObject<HTMLInputElement> | null;
  autocompleteId?: string;
  autocompleteRestrictedCountries?: string[];
  autocompleteResultTypes?: string[];
  precision?: number;
}

const useGoogleMapsApi = ({
  autocompleteRef,
  autocompleteId,
  defaultLocation,
  autocompleteRestrictedCountries = ['br'],
  autocompleteResultTypes = ['(cities)'],
  precision = 9,
}: GoogleMapsApiArgs) => {
  const [address, setAddress] = useState('');
  const [addressLocation, setAddressLocation] = useState<ILocation | undefined>(
    defaultLocation,
  );
  const [possibleLocal, setPossibleLocal] = useState<PossibleLocal>({});
  const [loadingAddress, setLoadingAddress] = useState(false);

  useEffect(() => {
    if (!address || (!autocompleteRef?.current && !autocompleteId)) {
      return;
    }

    let autocompleteReference: HTMLInputElement | undefined;

    if (autocompleteRef?.current) {
      autocompleteReference = autocompleteRef.current;
    } else if (autocompleteId) {
      autocompleteReference = document.getElementById(
        autocompleteId,
      ) as HTMLInputElement;
    }

    if (!autocompleteReference) {
      return;
    }

    const autocomplete = new window.google.maps.places.Autocomplete(
      autocompleteReference,
      {
        types: autocompleteResultTypes,
        componentRestrictions: { country: autocompleteRestrictedCountries },
      },
    );

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();

      if (place.formatted_address) {
        onChangeAddress(place.formatted_address);
      } else if (place.name) {
        onChangeAddress(place.name);
      }

      const ufAddress = place.address_components?.find(addressComponent =>
        isState(addressComponent),
      );

      setPossibleLocal({ city: place.name, uf: ufAddress?.long_name });

      if (place.geometry && place.geometry.location) {
        setLoadingAddress(true);

        const latitude = place.geometry.location.lat();
        const longitude = place.geometry.location.lng();

        const newAddressLocation: ILocation = {
          latitude: Number(latitude.toPrecision(precision)),
          longitude: Number(longitude.toPrecision(precision)),
        };

        setAddressLocation(newAddressLocation);

        setTimeout(() => setLoadingAddress(false), 1000);
      }
    });
  }, [address]);

  useEffect(() => {
    if (defaultLocation) {
      getLocation(defaultLocation);
    }
  }, []);

  function onChangeAddress(value: string) {
    setAddress(value);
  }

  async function getLocation(location: ILocation) {
    const { latitude, longitude } = location;

    setLoadingAddress(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=jsonv2&zoom=10`,
      );
      const position = response ? await response.json() : null;

      if (position) {
        const cityName =
          position.address.city ||
          position.address.city_district ||
          position.address.town ||
          position.name;

        setPossibleLocal({ city: cityName, uf: position.address?.state });

        // Lagarto, Sergipe, Brazil
        setAddress(
          `${cityName}, ${position.address.state}, ${position.address.country}`.replace(
            /undefined/g,
            '',
          ),
        );
      }
    } finally {
      setLoadingAddress(false);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function isState(component: any) {
    if (component?.types?.includes('administrative_area_level_1')) {
      return true;
    }

    return false;
  }

  return {
    address,
    addressLocation,
    loadingAddress,
    onChangeAddress,
    autocompleteRef,
    possibleLocal,
  };
};

export default useGoogleMapsApi;
